/**
 * PATH: apps/qa/src/pages/reports/QaReportSheet.tsx
 * FILE_ID: QA-REPORTS-SHEET-001
 * PURPOSE: Generated QA reports and drafts on the People contacts grid.
 * WHY: The card grid hid type, job, counts, and status in a tall tile.
 * NON-RESPONSIBILITIES: Does not generate or download. The page passes those actions.
 */

import { CheckCircle, Download, Link2, Loader2, Mail, RefreshCw, AlertTriangle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { DirectorySheet, type DirectoryColumn } from '@quotech/ui'
import { formatDate } from '@/lib/utils'
import type { ReportDraftListItem } from '@/lib/api'
import type { Report } from '@/types/qa'
import { getStatusLabel, getStatusStyles } from './report-helpers'

type DraftKey = 'title' | 'job' | 'sections' | 'author' | 'status'
type ReportKey = 'name' | 'type' | 'job' | 'inspection' | 'status' | 'areas' | 'items' | 'generated' | 'actions'

const DRAFT_COLUMNS: DirectoryColumn<DraftKey>[] = [
  { key: 'title', label: 'Title', width: 'min-w-[14rem]' },
  { key: 'job', label: 'Job', width: 'min-w-[6rem]' },
  { key: 'sections', label: 'Sections', align: 'right', width: 'min-w-[6rem]' },
  { key: 'author', label: 'Author', width: 'min-w-[8rem]' },
  { key: 'status', label: 'Status', width: 'min-w-[7rem]' },
]

const REPORT_COLUMNS: DirectoryColumn<ReportKey>[] = [
  { key: 'name', label: 'Report', width: 'min-w-[14rem]' },
  { key: 'type', label: 'Type', width: 'min-w-[8rem]' },
  { key: 'job', label: 'Job', width: 'min-w-[6rem]' },
  { key: 'inspection', label: 'Inspection', width: 'min-w-[10rem]' },
  { key: 'status', label: 'Status', width: 'min-w-[8rem]' },
  { key: 'areas', label: 'Areas', align: 'right', width: 'min-w-[5rem]' },
  { key: 'items', label: 'Items', align: 'right', width: 'min-w-[5rem]' },
  { key: 'generated', label: 'Generated', width: 'min-w-[120px]' },
  { key: 'actions', label: '', width: 'min-w-[8rem]' },
]

function typeLabel(type: string) {
  return type.replace(/_/g, ' ')
}

export function QaDraftSheet({ drafts }: { drafts: ReportDraftListItem[] }) {
  if (!drafts.length) return null
  return (
    <DirectorySheet
      label="Editable report drafts"
      columns={DRAFT_COLUMNS}
      rows={drafts}
      rowKey={(draft) => draft.id}
      renderCell={(column, draft) => {
        if (column.key === 'title') {
          return (
            <Link to={`/reports/draft/${draft.id}`} className="font-medium text-foreground no-underline hover:underline">
              {draft.title}
            </Link>
          )
        }
        if (column.key === 'job') return `#${draft.job_id}`
        if (column.key === 'sections') return `${draft.completed_sections}/${draft.section_count}`
        if (column.key === 'author') return draft.created_by_name || '—'
        return <span className="capitalize">{draft.status}</span>
      }}
    />
  )
}

export function QaReportSheet({
  reports,
  onDownload,
  onShare,
  onEmail,
  onRegenerate,
}: {
  reports: Report[]
  onDownload: (report: Report) => void
  onShare: (report: Report) => void
  onEmail: (report: Report) => void
  onRegenerate: (report: Report) => void
}) {
  return (
    <DirectorySheet
      label="QA reports"
      columns={REPORT_COLUMNS}
      rows={reports}
      rowKey={(report) => report.id}
      renderCell={(column, report) => reportCell(column.key, report, { onDownload, onShare, onEmail, onRegenerate })}
    />
  )
}

function reportCell(
  key: ReportKey,
  report: Report,
  actions: {
    onDownload: (report: Report) => void
    onShare: (report: Report) => void
    onEmail: (report: Report) => void
    onRegenerate: (report: Report) => void
  },
) {
  const status = getStatusStyles(report.status)
  if (key === 'name') {
    return (
      <span className="truncate font-medium">
        {report.report_name}
        {report.version > 1 ? <span className="ml-2 text-xs font-normal text-muted-foreground">v{report.version}</span> : null}
      </span>
    )
  }
  if (key === 'type') return <span className="capitalize">{typeLabel(report.report_type)}</span>
  if (key === 'job') return report.job_id ? `#${report.job_id}` : '—'
  if (key === 'inspection') return <span className="truncate">{report.inspection_name || '—'}</span>
  if (key === 'status') {
    return (
      <span className={`inline-flex items-center gap-1 ${status.text}`}>
        {(report.status === 'generating' || report.status === 'queued') && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
        {report.status === 'generated' && <CheckCircle className="h-3.5 w-3.5" aria-hidden />}
        {report.status === 'failed' && <AlertTriangle className="h-3.5 w-3.5" aria-hidden />}
        {getStatusLabel(report.status)}
      </span>
    )
  }
  if (key === 'areas') return report.area_count
  if (key === 'items') return report.checklist_item_count
  if (key === 'generated') return <span className="text-muted-foreground">{formatDate(report.generated_at)}</span>
  return (
    <div className="flex items-center justify-end gap-1" onClick={(event) => event.stopPropagation()}>
      {report.status === 'generated' ? (
        <>
          <button type="button" onClick={() => actions.onDownload(report)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-foreground hover:bg-accent" aria-label={`Download ${report.report_name}`}>
            <Download className="h-3.5 w-3.5" aria-hidden />
            PDF
          </button>
          <button type="button" onClick={() => actions.onShare(report)} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" aria-label={`Share ${report.report_name}`}>
            <Link2 className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button type="button" onClick={() => actions.onEmail(report)} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" aria-label={`Email ${report.report_name}`}>
            <Mail className="h-3.5 w-3.5" aria-hidden />
          </button>
        </>
      ) : null}
      {report.status === 'failed' ? (
        <button type="button" onClick={() => actions.onRegenerate(report)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:bg-accent">
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Retry
        </button>
      ) : null}
      {report.error_message ? <p className="mt-1 max-w-[14rem] text-xs text-destructive">{report.error_message}</p> : null}
    </div>
  )
}
